import { useEffect, useMemo, useState } from 'react';
import { CloseIcon, PlusIcon, TrashIcon } from '@/components/catalog/icons';
import {
  Badge,
  Button,
  ConfirmDialog,
  EmptyState,
  Field,
  Input,
  Panel,
  PanelBody,
  PanelHeader,
  Skeleton,
  TBody,
  TD,
  TH,
  THead,
  TR,
  Table,
  TableWrap,
} from '@/components/ui';
import { Money } from '@/components/ui/Money';
import { Notice } from '@/components/ui/Notice';
import { ApiError, api } from '@/lib/api';
import { pluralise } from '@/lib/format';
import { denialReason } from '@/lib/permissions';
import type { PriceTier, Project, ProjectPriceEntryWire } from '@/lib/types';
import { useAuth } from '@/lib/use-auth';
import { invalidate } from '@/lib/use-project-prices';

/*
 * PROJECT PRICING (owner rulings 2026-10-27, "Pricing Sheets - 2027 Events").
 *
 * The grid of price entries for one project: every product the project sells
 * × currency (USD / GBP / EUR) × price tier (the `f_price_tiers` master, active
 * rows, in `sortOrder`). Each cell holds a per-city rate, a flat price and a
 * list of overrides. The pricing RULES are `lib/project-pricing.ts`; this file
 * only edits the stored set.
 *
 * ## The write is a WHOLE-SET replace
 *
 * `PUT /projects/:id/prices` replaces every entry the project has. So Save
 * always sends the full set as it stands on screen, and an entry left out is an
 * entry DELETED. That is why:
 *
 *  - an entry whose rate AND flat price are both empty (and that carries no
 *    override) is the one way to remove a price — it is simply not sent;
 *  - an entry with an INVALID value refuses the save on the client rather than
 *    being dropped: dropping it would silently delete a stored price;
 *  - entries the grid would not otherwise draw — a product no longer on the
 *    project, a retired tier, a currency outside the three — are drawn anyway
 *    and SENT, so the server's 422 names them and a person removes them
 *    deliberately. Leaving them out of the grid would delete them unseen.
 *
 * ## Overrides
 *
 * A per-city entry's override is an exact CITY COUNT ("4 cities"); a flat
 * entry's is a line QUANTITY ("Qty 2") and its price is the LINE TOTAL. Which
 * one an override means follows whether the per-city rate is filled in, the
 * same test `resolveProjectPrice` makes. A wholly blank override row is
 * ignored, so "Add override" can never block a save on its own.
 *
 * ## Errors
 *
 * Server 422 `details` arrive as `entries.N…` paths against the array that was
 * SENT, which is shorter than the grid (empty entries and blank override rows
 * are not sent). `sentMap` records, per sent index, which cell and which
 * override rows it came from, so a refusal lands on the control that caused it.
 *
 * Money is a decimal string end to end: validated by pattern, never parsed.
 */

const CURRENCIES = ['USD', 'GBP', 'EUR'] as const;

/** DECIMAL(12,2) on the rate and flat price; DECIMAL(14,2) on an override. */
const RATE_PATTERN = /^\d{1,10}(\.\d{1,2})?$/;
const OVERRIDE_PRICE_PATTERN = /^\d{1,12}(\.\d{1,2})?$/;
/** INT UNSIGNED, kept well inside the safe-integer range. */
const UNIT_COUNT_PATTERN = /^\d{1,9}$/;

const MONEY_ERROR = 'Enter an amount, up to 2 decimal places.';
const UNIT_COUNT_ERROR = 'Enter a whole number, 1 or more.';
const DUPLICATE_ERROR = 'Already used on this price.';
const NO_BASE_ERROR = 'Enter a per-city rate or a flat price, or remove the overrides.';
const NOT_ALLOWED = 'Your permission set does not include this. An administrator can change that.';

interface DraftOverride {
  id: string;
  unitCount: string;
  price: string;
}

interface DraftEntry {
  perCityRate: string;
  flatPrice: string;
  overrides: DraftOverride[];
}

type DraftMap = Record<string, DraftEntry>;

const EMPTY_ENTRY: DraftEntry = { perCityRate: '', flatPrice: '', overrides: [] };

type LoadState =
  | { status: 'loading' }
  | { status: 'ready'; entries: ProjectPriceEntryWire[]; tiers: PriceTier[] }
  | { status: 'error'; message: string };

interface SentEntry {
  key: string;
  overrideIds: string[];
}

interface ProductRow {
  id: number;
  name: string;
  /** False for a product a stored entry names but the project no longer sells. */
  onProject: boolean;
}

interface TierCol {
  id: number;
  name: string;
  retired: boolean;
}

interface Cell {
  key: string;
  product: ProductRow;
  currency: string;
  tier: TierCol;
}

let overrideSeq = 0;
function newOverrideId(): string {
  overrideSeq += 1;
  return `n${overrideSeq}`;
}

function entryKey(productId: number, currency: string, tierId: number): string {
  return `${productId}|${currency}|${tierId}`;
}

function domId(prefix: string, key: string, suffix: string): string {
  return `${prefix}-${key.replace(/\|/g, '-')}-${suffix}`;
}

function clean(value: string): string {
  return value.trim().replace(/,/g, '');
}

function isBlankOverride(override: DraftOverride): boolean {
  return clean(override.unitCount) === '' && clean(override.price) === '';
}

function isEmptyEntry(entry: DraftEntry): boolean {
  return (
    clean(entry.perCityRate) === '' &&
    clean(entry.flatPrice) === '' &&
    entry.overrides.every(isBlankOverride)
  );
}

function toDraft(entries: readonly ProjectPriceEntryWire[]): DraftMap {
  const map: DraftMap = {};
  for (const entry of entries) {
    map[entryKey(entry.productId, entry.currency.toUpperCase(), entry.tierId)] = {
      perCityRate: entry.perCityRate ?? '',
      flatPrice: entry.flatPrice ?? '',
      overrides: entry.overrides.map((override, index) => ({
        id: `s${index}`,
        unitCount: String(override.unitCount),
        price: override.price,
      })),
    };
  }
  return map;
}

/** A comparable fingerprint of the set a Save would send. */
function fingerprint(map: DraftMap): string {
  return JSON.stringify(
    Object.keys(map)
      .filter((key) => !isEmptyEntry(map[key] as DraftEntry))
      .sort()
      .map((key) => {
        const entry = map[key] as DraftEntry;
        return [
          key,
          clean(entry.perCityRate),
          clean(entry.flatPrice),
          entry.overrides
            .filter((o) => !isBlankOverride(o))
            .map((o) => [clean(o.unitCount), clean(o.price)]),
        ];
      }),
  );
}

function parseKey(key: string): { productId: number; currency: string; tierId: number } {
  const [productId = '0', currency = '', tierId = '0'] = key.split('|');
  return { productId: Number(productId), currency, tierId: Number(tierId) };
}

/**
 * Client checks, and the payload. Errors are keyed `<cell>:perCityRate`,
 * `<cell>:flatPrice`, `<cell>:entry` and `<cell>:o:<overrideId>:<field>`.
 */
function buildPayload(map: DraftMap): {
  errors: Record<string, string>;
  payload: ProjectPriceEntryWire[];
  sent: SentEntry[];
} {
  const errors: Record<string, string> = {};
  const payload: ProjectPriceEntryWire[] = [];
  const sent: SentEntry[] = [];

  for (const key of Object.keys(map).sort()) {
    const entry = map[key] as DraftEntry;
    if (isEmptyEntry(entry)) continue;

    const rate = clean(entry.perCityRate);
    const flat = clean(entry.flatPrice);
    if (rate !== '' && !RATE_PATTERN.test(rate)) errors[`${key}:perCityRate`] = MONEY_ERROR;
    if (flat !== '' && !RATE_PATTERN.test(flat)) errors[`${key}:flatPrice`] = MONEY_ERROR;
    if (rate === '' && flat === '') errors[`${key}:entry`] = NO_BASE_ERROR;

    const seen = new Set<number>();
    const overrides: ProjectPriceEntryWire['overrides'] = [];
    const overrideIds: string[] = [];
    for (const override of entry.overrides) {
      if (isBlankOverride(override)) continue;
      const count = clean(override.unitCount);
      const price = clean(override.price);
      const base = `${key}:o:${override.id}`;
      const countNumber = Number(count);
      if (!UNIT_COUNT_PATTERN.test(count) || countNumber < 1) {
        errors[`${base}:unitCount`] = UNIT_COUNT_ERROR;
      } else if (seen.has(countNumber)) {
        errors[`${base}:unitCount`] = DUPLICATE_ERROR;
      } else {
        seen.add(countNumber);
      }
      if (!OVERRIDE_PRICE_PATTERN.test(price)) errors[`${base}:price`] = MONEY_ERROR;
      overrides.push({ unitCount: countNumber, price });
      overrideIds.push(override.id);
    }

    const { productId, currency, tierId } = parseKey(key);
    payload.push({
      productId,
      currency,
      tierId,
      perCityRate: rate === '' ? null : rate,
      flatPrice: flat === '' ? null : flat,
      overrides,
    });
    sent.push({ key, overrideIds });
  }

  return { errors, payload, sent };
}

const SERVER_PATH = /^entries\.(\d+)(?:\.([A-Za-z]+)(?:\.(\d+)(?:\.([A-Za-z]+))?)?)?$/;

/** Route a 422's `details` onto the cells they name, by the SENT index. */
function routeServerErrors(
  error: ApiError,
  sent: readonly SentEntry[],
): { fields: Record<string, string>; banner: string[] } {
  const fields: Record<string, string> = {};
  const banner: string[] = [...error.formMessages];
  const put = (fieldKey: string, message: string) => {
    fields[fieldKey] = fields[fieldKey] ? `${fields[fieldKey]} ${message}` : message;
  };
  for (const [path, message] of Object.entries(error.fieldErrors)) {
    const match = SERVER_PATH.exec(path);
    const target = match ? sent[Number(match[1])] : undefined;
    if (!match || !target) {
      banner.push(message);
      continue;
    }
    const field = match[2];
    if (field === 'perCityRate' || field === 'flatPrice') {
      put(`${target.key}:${field}`, message);
    } else if (field === 'overrides' && match[3] !== undefined) {
      const overrideId = target.overrideIds[Number(match[3])];
      const sub = match[4] === 'unitCount' || match[4] === 'price' ? match[4] : 'price';
      if (overrideId) put(`${target.key}:o:${overrideId}:${sub}`, message);
      else put(`${target.key}:entry`, message);
    } else {
      put(`${target.key}:entry`, message);
    }
  }
  if (Object.keys(fields).length === 0 && banner.length === 0) banner.push(error.message);
  return { fields, banner };
}

function sortTiers(tiers: readonly PriceTier[]): PriceTier[] {
  return [...tiers].sort((a, b) => a.sortOrder - b.sortOrder || a.id - b.id);
}

// --- presentational pieces ------------------------------------------------

function FieldError({ id, message }: { id: string; message: string | undefined }) {
  if (!message) return null;
  return (
    <p id={id} className="m-0 mt-1 text-xs text-danger [overflow-wrap:anywhere]">
      {message}
    </p>
  );
}

function ReadOnlyMoney({ amount, currency }: { amount: string; currency: string }) {
  if (clean(amount) === '') return <span className="text-fg-subtle">—</span>;
  return <Money amount={amount} currency={currency} />;
}

function overrideLabel(perCity: boolean, unitCount: string): string {
  const count = Number(clean(unitCount));
  return perCity ? pluralise(count, 'city', 'cities') : `Qty ${count}`;
}

interface CellProps {
  cell: Cell;
  entry: DraftEntry;
  errors: Record<string, string>;
  editable: boolean;
  disabled: boolean;
  /** `t` (table) or `c` (cards): both copies are mounted, so ids must differ. */
  idPrefix: string;
  onChange: (patch: Partial<DraftEntry>) => void;
}

function contextLabel(cell: Cell): string {
  return `${cell.product.name}, ${cell.currency}, ${cell.tier.name}`;
}

function AmountInput({
  cell,
  entry,
  errors,
  editable,
  disabled,
  idPrefix,
  onChange,
  field,
  label,
}: CellProps & { field: 'perCityRate' | 'flatPrice'; label: string }) {
  const value = entry[field];
  if (!editable) return <ReadOnlyMoney amount={value} currency={cell.currency} />;
  const error = errors[`${cell.key}:${field}`];
  const errorId = domId(idPrefix, cell.key, `${field}-error`);
  return (
    <div className="min-w-[8rem]">
      <Input
        inputMode="decimal"
        autoComplete="off"
        aria-label={`${label} — ${contextLabel(cell)}`}
        aria-describedby={error ? errorId : undefined}
        invalid={Boolean(error)}
        disabled={disabled}
        value={value}
        onChange={(event) => onChange({ [field]: event.target.value })}
      />
      <FieldError id={errorId} message={error} />
    </div>
  );
}

function OverridesEditor({ cell, entry, errors, editable, disabled, idPrefix, onChange }: CellProps) {
  const perCity = clean(entry.perCityRate) !== '';
  const rows = editable ? entry.overrides : entry.overrides.filter((o) => !isBlankOverride(o));

  if (!editable) {
    if (rows.length === 0) return <span className="text-fg-subtle">—</span>;
    return (
      <ul className="m-0 list-none space-y-0.5 p-0">
        {rows.map((override) => (
          <li key={override.id} className="flex flex-wrap items-baseline gap-x-2">
            <span className="text-fg-secondary">{overrideLabel(perCity, override.unitCount)}</span>
            <Money amount={override.price} currency={cell.currency} />
          </li>
        ))}
      </ul>
    );
  }

  const update = (id: string, patch: Partial<DraftOverride>) =>
    onChange({
      overrides: entry.overrides.map((o) => (o.id === id ? { ...o, ...patch } : o)),
    });

  return (
    <div className="grid gap-2">
      {rows.length > 0 && (
        <ul className="m-0 grid list-none gap-2 p-0">
          {rows.map((override, index) => {
            const countError = errors[`${cell.key}:o:${override.id}:unitCount`];
            const priceError = errors[`${cell.key}:o:${override.id}:price`];
            const countErrorId = domId(idPrefix, cell.key, `o-${override.id}-count-error`);
            const priceErrorId = domId(idPrefix, cell.key, `o-${override.id}-price-error`);
            const count = Number(clean(override.unitCount));
            return (
              <li key={override.id} className="grid gap-1">
                <div className="flex flex-wrap items-center gap-2">
                  <div className="flex items-center gap-1.5">
                    {!perCity && <span className="text-xs text-fg-muted">Qty</span>}
                    <div className="w-20">
                      <Input
                        inputMode="numeric"
                        autoComplete="off"
                        aria-label={`${perCity ? 'Cities' : 'Quantity'}, override ${index + 1} — ${contextLabel(cell)}`}
                        aria-describedby={countError ? countErrorId : undefined}
                        invalid={Boolean(countError)}
                        disabled={disabled}
                        value={override.unitCount}
                        onChange={(event) => update(override.id, { unitCount: event.target.value })}
                      />
                    </div>
                    {perCity && (
                      <span className="text-xs text-fg-muted">
                        {Number.isFinite(count) && count === 1 ? 'city' : 'cities'}
                      </span>
                    )}
                  </div>
                  <div className="w-36">
                    <Input
                      inputMode="decimal"
                      autoComplete="off"
                      aria-label={`${perCity ? 'Price' : 'Line total'}, override ${index + 1} — ${contextLabel(cell)}`}
                      aria-describedby={priceError ? priceErrorId : undefined}
                      invalid={Boolean(priceError)}
                      disabled={disabled}
                      value={override.price}
                      onChange={(event) => update(override.id, { price: event.target.value })}
                    />
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    iconOnly
                    aria-label={`Remove override ${index + 1} — ${contextLabel(cell)}`}
                    title="Remove override"
                    leadingIcon={<CloseIcon />}
                    disabled={disabled}
                    onClick={() =>
                      onChange({ overrides: entry.overrides.filter((o) => o.id !== override.id) })
                    }
                  />
                </div>
                <FieldError id={countErrorId} message={countError} />
                <FieldError id={priceErrorId} message={priceError} />
              </li>
            );
          })}
        </ul>
      )}
      <div>
        <Button
          variant="ghost"
          size="sm"
          leadingIcon={<PlusIcon />}
          disabled={disabled}
          aria-label={`Add override — ${contextLabel(cell)}`}
          onClick={() =>
            onChange({
              overrides: [...entry.overrides, { id: newOverrideId(), unitCount: '', price: '' }],
            })
          }
        >
          Add override
        </Button>
      </div>
    </div>
  );
}

function TierName({ tier }: { tier: TierCol }) {
  return (
    <span className="flex flex-wrap items-center gap-1.5">
      <span>{tier.name}</span>
      {tier.retired && (
        <Badge tone="warning" variant="outline">
          Retired
        </Badge>
      )}
    </span>
  );
}

function ProductName({ product }: { product: ProductRow }) {
  return (
    <span className="flex flex-wrap items-center gap-2">
      <span className="[overflow-wrap:anywhere]">{product.name}</span>
      {!product.onProject && (
        <Badge tone="warning" variant="outline">
          Not on this project
        </Badge>
      )}
    </span>
  );
}

function PricingSkeleton() {
  return (
    <PanelBody>
      <div className="grid gap-2">
        {[0, 1, 2, 3].map((row) => (
          <Skeleton key={row} className="h-9 w-full" />
        ))}
      </div>
    </PanelBody>
  );
}

// --- the section ----------------------------------------------------------

/**
 * `bare` drops the "Pricing" heading for a host that already names the editor
 * (`ProjectPricingDialog`, titled "Prices · <project>"); Save and Discard keep
 * their own row at the top.
 */
export function ProjectPricingSection({ project, bare = false }: { project: Project; bare?: boolean }) {
  const { ability } = useAuth();
  const canUpdate = ability.canUpdate('projects');

  const [nonce, setNonce] = useState(0);
  const loadKey = `${project.id}:${nonce}`;
  const [loaded, setLoaded] = useState<{ key: string; value: LoadState } | null>(null);
  const [draft, setDraft] = useState<{ key: string; map: DraftMap } | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [banner, setBanner] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const [savedNotice, setSavedNotice] = useState(false);
  const [removeTarget, setRemoveTarget] = useState<Cell | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    Promise.all([
      api.projectPrices(project.id, controller.signal),
      api.listPriceTiers(controller.signal),
    ])
      .then(([prices, tiers]) => {
        if (controller.signal.aborted) return;
        setLoaded({
          key: loadKey,
          value: { status: 'ready', entries: prices.data.entries, tiers: tiers.data },
        });
      })
      .catch((caught: unknown) => {
        if (controller.signal.aborted) return;
        setLoaded({
          key: loadKey,
          value: {
            status: 'error',
            message:
              caught instanceof ApiError && caught.status === 403
                ? NOT_ALLOWED
                : caught instanceof Error
                  ? caught.message
                  : 'The request did not complete.',
          },
        });
      });
    return () => controller.abort();
  }, [project.id, loadKey]);

  const current = loaded?.key === loadKey ? loaded.value : null;
  const state: LoadState = current ?? { status: 'loading' };

  const savedMap = useMemo<DraftMap>(
    () => (current?.status === 'ready' ? toDraft(current.entries) : {}),
    [current],
  );
  const map: DraftMap = draft?.key === loadKey ? draft.map : savedMap;
  const dirty = useMemo(() => fingerprint(map) !== fingerprint(savedMap), [map, savedMap]);

  const tiers = useMemo(
    () => (current?.status === 'ready' ? sortTiers(current.tiers) : []),
    [current],
  );
  const defaultTier = tiers.find((tier) => tier.isDefault) ?? null;

  /*
   * What the grid draws: every product on the project × the three currencies
   * × every ACTIVE tier — plus any cell a stored or drafted entry names that
   * would otherwise not be drawn (a product off the project, a retired tier,
   * another currency). Those are sent on Save like any other, so they must be
   * on screen.
   */
  const grid = useMemo(() => {
    const knownKeys = new Set([...Object.keys(savedMap), ...Object.keys(map)]);
    const tierById = new Map(tiers.map((tier) => [tier.id, tier]));

    const products: ProductRow[] = [];
    const seenProducts = new Set<number>();
    for (const product of project.products) {
      if (seenProducts.has(product.id)) continue;
      seenProducts.add(product.id);
      products.push({ id: product.id, name: product.name, onProject: true });
    }
    const currencies: string[] = [...CURRENCIES];
    const extraTierIds: number[] = [];
    for (const key of knownKeys) {
      const { productId, currency, tierId } = parseKey(key);
      if (!seenProducts.has(productId)) {
        seenProducts.add(productId);
        products.push({ id: productId, name: `Product #${productId}`, onProject: false });
      }
      if (!currencies.includes(currency)) currencies.push(currency);
      const tier = tierById.get(tierId);
      if ((!tier || !tier.isActive) && !extraTierIds.includes(tierId)) extraTierIds.push(tierId);
    }

    const columns: TierCol[] = [
      ...tiers.filter((tier) => tier.isActive).map((tier) => ({ id: tier.id, name: tier.name, retired: false })),
      ...extraTierIds.map((id) => ({
        id,
        name: tierById.get(id)?.name ?? `Tier #${id}`,
        retired: true,
      })),
    ];

    return products.map((product) => ({
      product,
      currencies: currencies
        .map((currency) => ({
          currency,
          cells: columns
            .filter((tier) => {
              const key = entryKey(product.id, currency, tier.id);
              const standard =
                product.onProject &&
                !tier.retired &&
                (CURRENCIES as readonly string[]).includes(currency);
              return standard || knownKeys.has(key);
            })
            .map((tier): Cell => ({ key: entryKey(product.id, currency, tier.id), product, currency, tier })),
        }))
        .filter((group) => group.cells.length > 0),
    }));
  }, [map, savedMap, tiers, project.products]);

  const activeTierCount = tiers.filter((tier) => tier.isActive).length;
  const editable = canUpdate;
  const inputsDisabled = saving;

  function patchCell(key: string, patch: Partial<DraftEntry>) {
    setSavedNotice(false);
    setDraft((previous) => {
      const base = previous?.key === loadKey ? previous.map : savedMap;
      const current = base[key] ?? EMPTY_ENTRY;
      return { key: loadKey, map: { ...base, [key]: { ...current, ...patch } } };
    });
    setErrors((previous) => {
      const next: Record<string, string> = {};
      for (const [errorKey, message] of Object.entries(previous)) {
        if (!errorKey.startsWith(`${key}:`)) next[errorKey] = message;
      }
      return next;
    });
  }

  function removeCell(key: string) {
    setSavedNotice(false);
    setDraft((previous) => {
      const base = previous?.key === loadKey ? previous.map : savedMap;
      const next = { ...base };
      delete next[key];
      return { key: loadKey, map: next };
    });
    setErrors((previous) => {
      const next: Record<string, string> = {};
      for (const [errorKey, message] of Object.entries(previous)) {
        if (!errorKey.startsWith(`${key}:`)) next[errorKey] = message;
      }
      return next;
    });
  }

  function discard() {
    setDraft(null);
    setErrors({});
    setBanner([]);
    setSavedNotice(false);
  }

  async function save() {
    if (state.status !== 'ready' || saving) return;
    const built = buildPayload(map);
    if (Object.keys(built.errors).length > 0) {
      setErrors(built.errors);
      setBanner(['Some prices need attention. Nothing was saved.']);
      setSavedNotice(false);
      return;
    }
    setSaving(true);
    setErrors({});
    setBanner([]);
    setSavedNotice(false);
    try {
      const response = await api.putProjectPrices(project.id, built.payload);
      setLoaded({
        key: loadKey,
        value: { status: 'ready', entries: response.data.entries, tiers: state.tiers },
      });
      setDraft(null);
      setSavedNotice(true);
      invalidate(project.id);
    } catch (caught) {
      if (caught instanceof ApiError && caught.status === 422) {
        const routed = routeServerErrors(caught, built.sent);
        setErrors(routed.fields);
        setBanner(
          routed.banner.length > 0 ? routed.banner : ['Some prices were refused. Nothing was saved.'],
        );
      } else if (caught instanceof ApiError && caught.status === 403) {
        setBanner([
          denialReason(ability, 'update', 'projects') ??
            'Your account no longer has permission to change project prices.',
        ]);
      } else {
        setBanner([caught instanceof Error ? caught.message : 'The save did not complete.']);
      }
    } finally {
      setSaving(false);
    }
  }

  const readOnlyReason = canUpdate
    ? null
    : (denialReason(ability, 'update', 'projects') ?? 'Your account cannot change project prices.');

  const actions =
    state.status === 'error' ? (
      <Button size="sm" onClick={() => setNonce((n) => n + 1)}>
        Try again
      </Button>
    ) : state.status === 'ready' ? (
      <>
        {savedNotice && (
          <span role="status" className="text-xs text-fg-muted">
            Saved.
          </span>
        )}
        {canUpdate && dirty && (
          <Button size="sm" variant="secondary" disabled={saving} onClick={discard}>
            Discard
          </Button>
        )}
        <Button
          size="sm"
          variant="primary"
          loading={saving}
          disabled={!canUpdate || !dirty}
          title={readOnlyReason ?? (dirty ? undefined : 'Nothing has changed.')}
          onClick={() => void save()}
        >
          Save prices
        </Button>
      </>
    ) : null;

  const removeDescription = removeTarget
    ? removeTarget.tier.id === defaultTier?.id || defaultTier === null
      ? `The per-city rate, flat price and overrides for ${removeTarget.currency} go when you save. New lines selling ${removeTarget.product.name} will then use the catalogue price.`
      : `The per-city rate, flat price and overrides for ${removeTarget.currency} go when you save. New ${removeTarget.tier.name} lines selling ${removeTarget.product.name} will then use the ${defaultTier.name} price, or the catalogue price where there is none.`
    : '';

  function renderRemove(cell: Cell, entry: DraftEntry) {
    if (!editable || isEmptyEntry(entry)) return null;
    return (
      <Button
        variant="ghost"
        size="sm"
        iconOnly
        aria-label={`Remove price — ${contextLabel(cell)}`}
        title="Remove price"
        leadingIcon={<TrashIcon />}
        disabled={inputsDisabled}
        onClick={() => setRemoveTarget(cell)}
      />
    );
  }

  return (
    <Panel className="min-w-0" aria-labelledby={bare ? undefined : 'project-pricing-heading'}>
      {bare ? (
        actions && (
          <div className="flex flex-wrap items-center justify-end gap-2 border-b border-border px-4 py-2">
            {actions}
          </div>
        )
      ) : (
        <PanelHeader id="project-pricing-heading" title="Pricing" actions={actions} />
      )}

      {state.status === 'loading' && <PricingSkeleton />}

      {state.status === 'error' && (
        <PanelBody>
          <Notice tone="danger" role="alert" title="Couldn’t load the prices.">
            <p className="m-0 min-w-0 [overflow-wrap:anywhere]">{state.message}</p>
          </Notice>
        </PanelBody>
      )}

      {state.status === 'ready' && banner.length > 0 && (
        <PanelBody className="border-b border-border">
          <Notice tone="danger" role="alert" title="The prices weren’t saved.">
            {banner.map((line, index) => (
              <p key={index} className="m-0 min-w-0 [overflow-wrap:anywhere]">
                {line}
              </p>
            ))}
          </Notice>
        </PanelBody>
      )}

      {state.status === 'ready' && grid.length === 0 && (
        <EmptyState
          title="No products on this project"
          description="Add a product on the edit form to give it prices."
        />
      )}

      {state.status === 'ready' && grid.length > 0 && activeTierCount === 0 && (
        <PanelBody className="border-b border-border">
          <Notice tone="warning" title="No active price tiers." />
        </PanelBody>
      )}

      {state.status === 'ready' && grid.length > 0 && (
        <>
          <div className="hidden md:block">
            <TableWrap>
              <Table className="min-w-[960px]">
                <THead>
                  <TR>
                    <TH>Currency</TH>
                    <TH>Tier</TH>
                    <TH>Per-city rate</TH>
                    <TH>Flat price</TH>
                    <TH>Overrides</TH>
                    {editable && (
                      <TH>
                        <span className="sr-only">Actions</span>
                      </TH>
                    )}
                  </TR>
                </THead>
                <TBody>
                  {grid.map(({ product, currencies }) => [
                    <TR key={`p-${product.id}`}>
                      <TD
                        colSpan={editable ? 6 : 5}
                        className="bg-surface-sunken font-semibold"
                      >
                        <ProductName product={product} />
                      </TD>
                    </TR>,
                    ...currencies.flatMap(({ currency, cells }) =>
                      cells.map((cell, index) => {
                        const entry = map[cell.key] ?? EMPTY_ENTRY;
                        const props: CellProps = {
                          cell,
                          entry,
                          errors,
                          editable,
                          disabled: inputsDisabled,
                          idPrefix: 't',
                          onChange: (patch) => patchCell(cell.key, patch),
                        };
                        const entryError = errors[`${cell.key}:entry`];
                        return (
                          <TR key={cell.key}>
                            {index === 0 && (
                              <TD verticalAlign="top" rowSpan={cells.length} className="font-medium">
                                {currency}
                              </TD>
                            )}
                            <TD verticalAlign="top">
                              <TierName tier={cell.tier} />
                              <FieldError
                                id={domId('t', cell.key, 'entry-error')}
                                message={entryError}
                              />
                            </TD>
                            <TD verticalAlign="top">
                              <AmountInput {...props} field="perCityRate" label="Per-city rate" />
                            </TD>
                            <TD verticalAlign="top">
                              <AmountInput {...props} field="flatPrice" label="Flat price" />
                            </TD>
                            <TD verticalAlign="top">
                              <OverridesEditor {...props} />
                            </TD>
                            {editable && (
                              <TD verticalAlign="top" className="text-right">
                                {renderRemove(cell, entry)}
                              </TD>
                            )}
                          </TR>
                        );
                      }),
                    ),
                  ])}
                </TBody>
              </Table>
            </TableWrap>
          </div>

          <div className="divide-y divide-border md:hidden">
            {grid.map(({ product, currencies }) => (
              <section key={`c-${product.id}`} className="grid gap-3 px-4 py-3">
                <h3 className="m-0 text-sm font-semibold text-fg">
                  <ProductName product={product} />
                </h3>
                {currencies.map(({ currency, cells }) => (
                  <div key={currency} className="grid gap-2">
                    <p className="m-0 text-xs font-medium text-fg-secondary">{currency}</p>
                    <ul className="m-0 grid list-none gap-2 p-0">
                      {cells.map((cell) => {
                        const entry = map[cell.key] ?? EMPTY_ENTRY;
                        const props: CellProps = {
                          cell,
                          entry,
                          errors,
                          editable,
                          disabled: inputsDisabled,
                          idPrefix: 'c',
                          onChange: (patch) => patchCell(cell.key, patch),
                        };
                        const entryError = errors[`${cell.key}:entry`];
                        return (
                          <li key={cell.key} className="grid gap-3 rounded-md border border-border p-3">
                            <div className="flex items-start justify-between gap-2">
                              <div className="min-w-0 text-sm font-medium text-fg">
                                <TierName tier={cell.tier} />
                              </div>
                              {renderRemove(cell, entry)}
                            </div>
                            <FieldError id={domId('c', cell.key, 'entry-error')} message={entryError} />
                            {editable ? (
                              <div className="grid gap-3 sm:grid-cols-2">
                                <Field label="Per-city rate" error={errors[`${cell.key}:perCityRate`]}>
                                  {(fieldProps) => (
                                    <Input
                                      {...fieldProps}
                                      inputMode="decimal"
                                      autoComplete="off"
                                      invalid={Boolean(errors[`${cell.key}:perCityRate`])}
                                      disabled={inputsDisabled}
                                      value={entry.perCityRate}
                                      onChange={(event) =>
                                        patchCell(cell.key, { perCityRate: event.target.value })
                                      }
                                    />
                                  )}
                                </Field>
                                <Field label="Flat price" error={errors[`${cell.key}:flatPrice`]}>
                                  {(fieldProps) => (
                                    <Input
                                      {...fieldProps}
                                      inputMode="decimal"
                                      autoComplete="off"
                                      invalid={Boolean(errors[`${cell.key}:flatPrice`])}
                                      disabled={inputsDisabled}
                                      value={entry.flatPrice}
                                      onChange={(event) =>
                                        patchCell(cell.key, { flatPrice: event.target.value })
                                      }
                                    />
                                  )}
                                </Field>
                              </div>
                            ) : (
                              <dl className="m-0 grid grid-cols-2 gap-x-3 gap-y-1 text-sm">
                                <dt className="text-xs text-fg-muted">Per-city rate</dt>
                                <dd className="m-0 text-right">
                                  <ReadOnlyMoney amount={entry.perCityRate} currency={currency} />
                                </dd>
                                <dt className="text-xs text-fg-muted">Flat price</dt>
                                <dd className="m-0 text-right">
                                  <ReadOnlyMoney amount={entry.flatPrice} currency={currency} />
                                </dd>
                              </dl>
                            )}
                            <div className="grid gap-1.5">
                              <p className="m-0 text-xs font-medium text-fg-secondary">Overrides</p>
                              <OverridesEditor {...props} />
                            </div>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                ))}
              </section>
            ))}
          </div>
        </>
      )}

      <ConfirmDialog
        open={removeTarget !== null}
        onClose={() => setRemoveTarget(null)}
        onConfirm={() => {
          if (removeTarget) removeCell(removeTarget.key);
          setRemoveTarget(null);
        }}
        title={removeTarget ? `Remove the ${removeTarget.tier.name} price for ${removeTarget.product.name}?` : 'Remove price?'}
        description={removeDescription}
        confirmLabel="Remove price"
      />
    </Panel>
  );
}
